import { useState } from "react";
import { Space, Button, Row, Col, Typography, TabsProps, Tabs, notification } from "antd";

import type { ColumnsType } from "antd/es/table";
import { EyeOutlined, EditOutlined, FilePdfOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { useGetInstallationsQuery } from "../../../redux/services/installationAPI";
import Filters from "../Filters";
import {
  getStatusText,
  localizeDateTime,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { Link, useNavigate } from "react-router-dom";
import { useSendCustomerReportMutation, useViewCustomerReportMutation } from "../../../redux/services/assignmentApi";
import TopScrollTable from "../../../components/TopScrollTable";

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "Payment Pending Installations"
  },
  {
    key: "2",
    label: "Payment Closed Installations"
  }
];

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

interface Installation {
  id: string;
  ecb_install_code: string;
}

const { Text } = Typography;

const PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM = new Date("2025-03-01");

const Installation = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    payment_received: false,
    page: parseInt(querySearchParams.page) || 1,
    ecb_code: parseInt(querySearchParams.ecb_code),
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const [tabKey, setTabKey] = useState("1");
  const [api, contextHolder] = notification.useNotification();


  const [viewCustomerReport] = useViewCustomerReportMutation();
  const [sendCustomerReport] = useSendCustomerReportMutation();

  const { data, isLoading, refetch, isFetching } = useGetInstallationsQuery({
    ...queryParams
  });
  const installations = data?.installations || [];

  const onSendCustomerReport = async (installationId: any) => {
    try {
      await sendCustomerReport(installationId).unwrap();
      api.success({
        message: "Customer report send successfully",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.error({
        message: "Customer report send operation failed",
        placement: "top"
      });
    }
  };

  const onViewCustomerReport = async (installationId: any) => {
    try {
      const res = await viewCustomerReport(installationId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
      refetch();
    } catch (error: any) {
      api.error({
        message: "MSIL IR report operation failed",
        placement: "top"
      });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Scheduled Installation Date",
      dataIndex: "scheduled_install_date_time",
      key: "scheduled_install_date_time",
      render: (scheduled_install_date_time) => (
        <Text>
          {scheduled_install_date_time ? localizeDateTime(scheduled_install_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <>{brand?.name}</>
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Installation Code",
      dataIndex: "ecb_install_code",
      key: "ecb_install_code",
      width: "150px",
      render: (ecb_install_code) => <>{ecb_install_code || "-"}</>
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => <>{assigned_city_manager?.name || "-"}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "MSIL IR Sending Status",
      dataIndex: "installation_report_sent_to_api",
      key: "installation_report_sent_to_api",
      render: (_, installation: any) => (
        <>
          {installation?.brand?.send_reports_to_api && installation?.status === "completed"
            ? installation?.installation_report_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "Partner",
      dataIndex: "install_partner",
      key: "install_partner",
      render: (install_partner) => <>{install_partner?.name || "-"}</>
    },

    {
      title: "Field Agent",
      dataIndex: "assigned_field_agent1",
      key: "assigned_field_agent1",
      render: (assigned_field_agent1) => <>{assigned_field_agent1?.name || "-"}</>
    },
    {
      title: "Installation Status",
      dataIndex: "status",
      key: "status",
      render: (_, installation: any) => <>{getStatusText(installation)}</>
    },
    {
      title: "Customer Rating",
      dataIndex: "customer_rating",
      key: "customer_rating",
      render: (_, installation: any) => (
        <>
          {installation.customer_rating && installation?.status === "completed"
            ? `${installation.customer_rating}/5`
            : "-"}
        </>
      )
    },
    {
      title: "Base Amount",
      dataIndex: "base_amount",
      key: "base_amount"
    },
    {
      title: "GST Amount",
      dataIndex: "gst_amount",
      key: "gst_amount"
    },
    {
      title: "Invoice Total",
      dataIndex: "invoice_total",
      key: "invoice_total"
    },
    {
      title: "Amount To Receive",
      dataIndex: "amount_to_receive",
      key: "amount_to_receive"
    },
    {
      title: "Amount To Refund",
      dataIndex: "amount_to_refund",
      key: "amount_to_refund"
    },
    {
      title: "Refund email sent?",
      dataIndex: "refund_email_sent",
      key: "refund_email_sent",
      render: (_, installation: any) => (
        <>
          {new Date(installation.created_at) < PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM
            ? ""
            : installation.refund_email_sent
            ? "Yes"
            : "No"}
        </>
      )
    },
    {
      title: "Refund processed email sent?",
      dataIndex: "refund_processed_email_sent",
      key: "refund_processed_email_sent",
      render: (_, installation: any) => (
        <>
          {new Date(installation.created_at) < PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM
            ? ""
            : installation.refund_processed_email_sent
            ? "Yes"
            : "No"}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, installation: any) => (
        <Space size="middle">
          {tabKey === "1" && (
            <>
              <Link to={`${installation?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Link to={`${installation?.id}/update`}>
                <Button icon={<EditOutlined />} title="Update"></Button>
              </Link>
            </>
          )}
          {tabKey === "2" && (
            <>
              <Link to={`${installation?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Link to={`${installation?.id}/update`}>
                <Button icon={<EditOutlined />} title="Update"></Button>
              </Link>
            </>
          )}
          {installation?.brand?.send_reports_to_api && (
            <>
              <Button
                icon={<FilePdfOutlined />}
                title="MSIL IR report PDF"
                onClick={() => onViewCustomerReport(installation.id)}
              ></Button>
              <Button
                icon={<CloudUploadOutlined />}
                title="Send MSIL IR report"
                onClick={() => onSendCustomerReport(installation.id)}
              ></Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const onChange = (key: string) => {
    setTabKey(key);
    if (key === "1") {
      const values: any = { payment_received: false };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "2") {
      const values: any = { payment_received: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col sm={24}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap"
            }}
          >
            <div>
              <h2>Installations</h2>
              <Text>Total installations: {data?.meta?.total_count || 0}</Text>
            </div>

            <Link to="upload-installation-invoice-claim">
              <Button size="large" type="primary">
                Upload Installation Invoices Claimed Date
              </Button>
            </Link>
          </div>
        </Col>
      </Row>
      <Tabs defaultActiveKey={tabKey} items={items} onChange={onChange} />
      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        installations
      />
      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={installations}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
    </>
  );
};

export default Installation;
